import * as React from "react";
import {
  Box, Paper, Stack, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Tooltip
} from "@mui/material";

import { useDispatch, useSelector } from "react-redux";

import { selectEvaluations, updateAxisRating } from "../store";

import {
  submitEvaluations,
  selectSubmissionStatus,
  selectSubmissionError,
  resetSubmissionState
} from "../services/assessmentService";

import {
  fetchStudents,
  selectStudentsList,
  selectStudentsStatus,
  selectStudentsError,
} from "../services/studentsService";

import SymbolRating from "./SymbolRating";
import SubmissionDialog from "./SubmissionDialog";
import FloatingSaveBar from "./FloatingSaveBar"; 

export default function EvaluationsForStudents() {

  const dispatch = useDispatch();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogStatus, setDialogStatus] = React.useState("success");

  const sessionState = useSelector((state) => state.session) || {};
  const { criteria, lesson, classInfo, subject, school, teacher } = sessionState;

  const localStudents = useSelector(selectStudentsList) || [];
  const studentsStatus = useSelector(selectStudentsStatus);
  const fetchStudentsError = useSelector(selectStudentsError);
  const isFetchingStudents = studentsStatus === 'loading';

  const reduxEvaluations = useSelector(selectEvaluations) || {};
  const submissionStatus = useSelector(selectSubmissionStatus);
  const submissionError = useSelector(selectSubmissionError);
  const isSaving = submissionStatus === 'loading';

  React.useEffect(() => {
    let classId = classInfo?.id || classInfo?._id;
    let schoolId = school?.id || school?._id;

    if (!classId) {
      const courseData = JSON.parse(localStorage.getItem("course") || "{}");
      classId = courseData?.class_id;
    }

    if (!schoolId) {
      schoolId = localStorage.getItem("schoolId");
    }

    if (classId && schoolId) {
      dispatch(fetchStudents({ classId, schoolId }));
    }

  }, [dispatch, classInfo, school]);

  const PIN_KEY = `pinned_students_${classInfo?.id || classInfo?._id || "default"}`;
  const [pinnedIds, setPinnedIds] = React.useState(new Set());

  React.useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PIN_KEY) || "[]");
      setPinnedIds(new Set(saved.map(String)));
    } catch {
      setPinnedIds(new Set());
    }
    dispatch(resetSubmissionState());
  }, [PIN_KEY, dispatch]);

  const orderedStudents = React.useMemo(() => {
    return [...localStudents].sort((a, b) => {
      const aId = String(a?.id || a?._id);
      const bId = String(b?.id || b?._id);
      const aPinned = pinnedIds.has(aId);
      const bPinned = pinnedIds.has(bId);
      return aPinned === bPinned ? 0 : aPinned ? -1 : 1;
    });
  }, [localStudents, pinnedIds]);

  const hasValidEvaluations = React.useMemo(() => {
    const list = Array.isArray(reduxEvaluations) ? reduxEvaluations : [];
    return list.some(student => {
      const ratings = student?.ratingsByAxis || {};
      return Object.values(ratings).some(val =>
        val !== "" && val !== null && val !== undefined && !isNaN(Number(val))
      );
    });
  }, [reduxEvaluations]);

  const evaluatedStudentsCount = React.useMemo(() => {
    if (orderedStudents.length === 0) return 0;
    const list = Array.isArray(reduxEvaluations) ? reduxEvaluations : [];

    let count = 0;
    orderedStudents.forEach((student) => {
      const studentId = String(student.id || student._id);
      const stData = list.find((s) => String(s.id) === studentId);
      if (stData) {
        const ratings = stData.ratingsByAxis || {};
        const hasAtLeastOneRating = Object.values(ratings).some(val =>
          val !== "" && val !== null && val !== undefined && !isNaN(Number(val))
        );
        if (hasAtLeastOneRating) count++;
      }
    });
    return count;
  }, [orderedStudents, reduxEvaluations]);

  const evaluationProgress = orderedStudents.length > 0
    ? Math.round((evaluatedStudentsCount / orderedStudents.length) * 100)
    : 0;

  const handleSaveToServer = async () => {

    const fallbackCourse = JSON.parse(localStorage.getItem("course") || "{}");
    const fallbackTeacher = JSON.parse(localStorage.getItem("teacher") || "{}");
    const now = new Date();

    // ✅ تم التعديل هنا: إضافة schoolId و lessonTitle للبيلود
    const payloadInfo = {
      time: lesson?.lessonDate || lesson?.date || now.toISOString(),
      date: lesson?.date || lesson?.lessonDate?.split('T')[0] || now.toISOString().split('T')[0],
      order: Number(lesson?.order || lesson?.lessonOrder || 1),
      classId: classInfo?.id || classInfo?._id || fallbackCourse?.class_id,
      courseId: subject?.id || subject?._id || lesson?.subjectId || fallbackCourse?.id,
      teacherId: teacher?.id || teacher?._id || fallbackTeacher?.id,
      schoolId: school?.id || school?._id || localStorage.getItem("schoolId"), // 👈 تمت الإضافة
      lessonTitle: lesson?.title || lesson?.lessonTitle || "بدون عنوان",       // 👈 تمت الإضافة
    };

    const list = Array.isArray(reduxEvaluations) ? reduxEvaluations : [];
    const payloadStudents = [];

    orderedStudents.forEach((student) => {

      const studentId = String(student.id || student._id);
      const stData = list.find((s) => String(s.id) === studentId);
      if (!stData) return;

      const ratingsObject = stData.ratingsByAxis || {};
      const assessments = [];

      Object.entries(ratingsObject).forEach(([axisId, value]) => {
        if (
          axisId !== "name" &&
          value !== "" &&
          value !== null &&
          value !== undefined &&
          !isNaN(Number(value))
        ) {
          assessments.push({
            id: String(axisId),
            result: Number(value)
          });
        }
      });

      if (assessments.length > 0) {
        payloadStudents.push({
          id: studentId,
          name: student.person?.full_name || student.name || "بدون اسم",
          assessments
        });
      }

    });

    if (payloadStudents.length === 0) {
      alert("لم يتم العثور على تقييمات. تأكد من تقييم طالب واحد على الأقل.");
      return;
    }

    const finalPayload = {
      ...payloadInfo,
      students: payloadStudents
    };

    try {
      console.log("بيانات الارسال:", JSON.stringify(finalPayload, null, 2));
      await dispatch(submitEvaluations(finalPayload)).unwrap();

      setDialogStatus("success");
      setDialogOpen(true);

    } catch (error) {
      console.error(error);
      setDialogStatus("error");
      setDialogOpen(true);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/login";
  };

  return (
    <Paper variant="section" sx={{p:'0px'}}>
        <TableContainer sx={{borderRadius:'10px'}} >
          <Table stickyHeader aria-label="evaluations table">

            <TableHead>
              <TableRow>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 'bold',
                    minWidth: 180, 
                    maxWidth: 220, 
                    whiteSpace: 'nowrap',
                    fontSize: '1rem',
                    position: 'sticky', 
                    right: 0, 
                    zIndex: 3, 
                    
                    boxShadow: '-4px 0px 8px -2px rgba(0,0,0,0.08)' 
                  }}
                >
                  اسم الطالب
                </TableCell>

                {criteria?.map((ax) => (
                  <TableCell
                    key={ax.id || ax._id}
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      minWidth: 240,
                      fontSize: '1rem'
                    }}
                  >
                    {ax.title}
                  </TableCell>
                ))}

              </TableRow>
            </TableHead>

            <TableBody>

              {orderedStudents.map((student, index) => {

                const studentId = String(student.id || student._id);
                const studentName = student.person?.full_name || student.name || "بدون اسم";

                const list = Array.isArray(reduxEvaluations) ? reduxEvaluations : [];
                const stData = list.find((s) => String(s.id) === studentId);

                // --- حساب نسبة الإنجاز لكل طالب ---
                const totalCriteria = criteria?.length || 0;
                let ratedCriteriaCount = 0;

                if (stData && stData.ratingsByAxis) {
                  criteria?.forEach(ax => {
                    const axId = String(ax.id || ax._id);
                    const val = stData.ratingsByAxis[axId];
                    if (val !== "" && val !== null && val !== undefined && !isNaN(Number(val))) {
                      ratedCriteriaCount++;
                    }
                  });
                }

                const studentProgressPercent = totalCriteria > 0 
                  ? Math.round((ratedCriteriaCount / totalCriteria) * 100) 
                  : 0;

                const tooltipText = `تم التقييم في ${ratedCriteriaCount} من أصل ${totalCriteria} محاور`;
                const isFullyEvaluated = studentProgressPercent === 100;
                // ------------------------------

                return (
                  <TableRow
                    key={studentId}
                    sx={{
                      backgroundColor:  'background.paper' 
                    }}
                  >

                    <TableCell 
                      sx={{ 
                        whiteSpace: 'nowrap', 
                        py: 1.5,
                        minWidth: 180, 
                        maxWidth: 240,
                        position: 'sticky',
                        right: 0, 
                        backgroundColor: 'inherit', 
                        zIndex: 1, 
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '-4px 0px 8px -2px rgba(0,0,0,0.08)' 
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        
                        {studentProgressPercent>0&&(
                          <Tooltip title={tooltipText} placement="top" arrow>
                          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                            <CircularProgress
                              variant="determinate"
                              value={100}
                              size={24}
                              thickness={5}
                              sx={{mx:'10px', color: 'var(--mui-palette-action-hover, rgba(0,0,0,0.1))' }}
                            />
                            <CircularProgress
                              variant="determinate"
                              value={studentProgressPercent}
                              size={24}
                              thickness={5}
                              color={isFullyEvaluated ? "success" : "primary"}
                              sx={{
                                mx:'10px',
                                position: 'absolute',
                                left: 0,
                                ...(studentProgressPercent === 0 && { color: 'transparent' })
                              }}
                            />
                          </Box>
                        </Tooltip>
                        )}

                        <Typography 
                          variant="text"
                          fontSize={'1rem'} 
                          fontWeight={600} 
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis' 
                          }}
                        >
                          {studentName}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {criteria?.map((ax) => {

                      const axisId = String(ax.id || ax._id);
                      const rating = stData?.ratingsByAxis?.[axisId] ?? "";

                      return (
                        <TableCell key={axisId} align="center" sx={{ py: 1 }}>
                          <SymbolRating
                          sx={{zIndex:1}}
                            value={rating}
                            onChange={(val) => {
                              dispatch(updateAxisRating({
                                id: studentId,
                                axisId: axisId,
                                rating: val === "" ? "" : Number(val),
                                name: studentName
                              }));
                            }}
                          />
                        </TableCell>
                      );
                    })}

                  </TableRow>
                );

              })}

            </TableBody>
          </Table>
        </TableContainer>

      <FloatingSaveBar
        evaluatedStudentsCount={evaluatedStudentsCount}
        totalStudents={orderedStudents.length}
        evaluationProgress={evaluationProgress}
        hasValidEvaluations={hasValidEvaluations}
        isSaving={isSaving}
        saveFailed={!!submissionError} 
        lessonDate={
          lesson?.lessonDate || 
          sessionState?.lessonDate || 
          lesson?.date || 
          JSON.parse(localStorage.getItem("course") || "{}")?.lessonDate || 
          new Date().toISOString()
        }
        Duration={lesson?.Duration || sessionState?.Duration} 
        onSave={handleSaveToServer}
        onLogout={handleLogout} 
      />

      <SubmissionDialog
        open={dialogOpen}
        status={dialogStatus}
        onClose={() => setDialogOpen(false)}
        onLogout={handleLogout}
        onRetry={() => {
          setDialogOpen(false);
          handleSaveToServer();
        }}
      />
    </Paper>
  );
}